import type { DesignTokens } from "../types/theme";

/**
 * Chronos Tracker — typographic system.
 * Clean geometric sans (Outfit) for the UI; stopwatch numerals use a
 * monospace with tabular figures (JetBrains Mono) defined via `font-mono`.
 */
const WEB_SYSTEM_FONT =
  "Outfit, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/**
 * Base tokens for Light mode.
 * Chronos identity: sober warm neutrals + gold accent ("time").
 */
export const LIGHT_BASE_TOKENS: DesignTokens = {
  background: "#f6f5f3",
  foreground: "#191a20",
  surface: "#ffffff",
  surfaceForeground: "#191a20",
  surfaceSecondary: "#f0efec",
  surfaceSecondaryForeground: "#191a20",
  surfaceTertiary: "#e7e5e1",
  surfaceTertiaryForeground: "#191a20",
  overlay: "#ffffff",
  overlayForeground: "#191a20",
  muted: "#71717a",
  default: "#f1f0ee",
  defaultForeground: "#191a20",
  accent: "#dc9400",
  accentForeground: "#241a05",
  danger: "#f43f5e",
  dangerForeground: "#ffffff",
  warning: "#f59e0b",
  warningForeground: "#191a20",
  success: "#10b981",
  successForeground: "#191a20",
  separator: "#b8b8ba",
  glassBackground: "rgba(255, 255, 255, 0.85)",
  glassBorder: "rgba(0, 0, 0, 0.1)",
  glassHighlight: "rgba(0, 0, 0, 0.08)",
  glow0: "rgba(245, 169, 42, 0.14)",
  glow1: "rgba(191, 114, 0, 0.1)",
  glow2: "rgba(220, 148, 0, 0.08)",
  mutedForeground: "#191a20",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.1)",
  overlayShadow: "rgba(0, 0, 0, 0.1)",
  fieldShadow: "rgba(0, 0, 0, 0.1)",
  fieldBorder: "rgba(0, 0, 0, 0.1)",
  fieldPlaceholder: "rgba(0, 0, 0, 0.4)",
  fieldBackground: "rgba(0, 0, 0, 0.03)",
  segment: "#ffffff",
  segmentForeground: "#191a20",
  link: "#191a20",
  focus: "#dc9400",
  fieldForeground: "#191a20",
  radius: "0.65rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#d9d7d2",
  primary: "#dc9400",
  primaryForeground: "#241a05",
  secondary: "#f0efec",
  secondaryForeground: "#191a20",
  backdrop: "rgba(0, 0, 0, 0.5)",
};

/**
 * Base tokens for Dark mode.
 * Chronos identity: deep dark base (focus mode) + gold accent.
 */
export const DARK_BASE_TOKENS: DesignTokens = {
  background: "#0b0b10",
  foreground: "#fafafa",
  surface: "#15171d",
  surfaceForeground: "#fafafa",
  surfaceSecondary: "#212229",
  surfaceSecondaryForeground: "#fafafa",
  surfaceTertiary: "#2e3037",
  surfaceTertiaryForeground: "#fafafa",
  overlay: "#1a1c22",
  overlayForeground: "#fafafa",
  muted: "#a1a1aa",
  default: "#2a2b32",
  defaultForeground: "#fafafa",
  accent: "#f1b12b",
  accentForeground: "#241a05",
  danger: "#e11d48",
  dangerForeground: "#ffffff",
  warning: "#f7ad30",
  warningForeground: "#191a20",
  success: "#10b981",
  successForeground: "#191a20",
  separator: "#3a3b42",
  glassBackground: "rgba(21, 23, 29, 0.92)",
  glassBorder: "rgba(255, 255, 255, 0.12)",
  glassHighlight: "rgba(255, 255, 255, 0.25)",
  glow0: "rgba(241, 177, 43, 0.45)",
  glow1: "rgba(191, 114, 0, 0.38)",
  glow2: "rgba(220, 148, 0, 0.3)",
  mutedForeground: "#fafafa",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.1)",
  overlayShadow: "rgba(0, 0, 0, 0.1)",
  fieldShadow: "rgba(0, 0, 0, 0.1)",
  fieldBorder: "rgba(255, 255, 255, 0.1)",
  fieldPlaceholder: "rgba(255, 255, 255, 0.4)",
  fieldBackground: "rgba(255, 255, 255, 0.03)",
  segment: "#3a3b42",
  segmentForeground: "#fafafa",
  link: "#fafafa",
  focus: "#f1b12b",
  fieldForeground: "#fafafa",
  radius: "0.65rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#2e3037",
  primary: "#f1b12b",
  primaryForeground: "#241a05",
  secondary: "#212229",
  secondaryForeground: "#fafafa",
  backdrop: "rgba(0, 0, 0, 0.7)",
};
